import { ArrowForwardIosSharp } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { getAllInvoiceAddressMembers } from 'src/shared/services/invoiceAddressService';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceAddressMemberResponse } from '../models/invoiceAddressMemberResponse';
import { InvoiceAddressMemberSearchFilter } from '../models/invoiceAddressMemberSearchFilter';

type InvoiceAddressConnectedMembersProps = {
  invoiceAddressId: number;
  searchString: string;
};

export const InvoiceAddressConnectedMembers = (props: InvoiceAddressConnectedMembersProps) => {
  const { invoiceAddressId, searchString } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const [expanded, setExpanded] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rows, setRows] = useState<InvoiceAddressMemberResponse[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'firstName', sort: 'desc' }]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }

    const searchFilter: InvoiceAddressMemberSearchFilter = {
      pageIndex: data.page,
      pageSize: data.pageSize,
      sortCol: sortModel[0].field,
      sortDir: sortModel[0].sort,
      filter: searchString,
      invoiceAddressId,
    };
    try {
      const result = await getAllInvoiceAddressMembers(searchFilter);
      const memberResult = extendObjectWithId(result.data.results, 'memberNr');

      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        setTimeout(() => {
          setRows(memberResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, invoiceAddressId, isMounted, searchString, sortModel]);

  useEffect(() => {
    if (!expanded) return;
    fetchData();
  }, [expanded, data.page, data.pageSize, sortModel, searchString, invoiceAddressId, fetchData]);

  const handleAccordionChange = (_e: React.SyntheticEvent, isExp: boolean) => {
    setExpanded(isExp);
  };

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };
  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const onRowClick = (params) => {
    if (params.row && params.row.member_id) {
      navigate(`${RoutesEnum.MemberDetails}/${params.row.member_id}`);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'memberNr',
      headerName: t('member:tableHeaders.memberNr'),
      flex: 0.7,
      renderCell: (params: GridCellParams<InvoiceAddressMemberResponse>) => {
        return <>{params.row.memberNr || ''}</>;
      },
    },
    {
      field: 'firstName',
      headerName: t('member:tableHeaders.firstName'),
      flex: 1,
      renderCell: (params: GridCellParams<InvoiceAddressMemberResponse>) => {
        return <>{params.row.firstName || ''}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('member:tableHeaders.lastName'),
      flex: 1,
      renderCell: (params: GridCellParams<InvoiceAddressMemberResponse>) => {
        return <>{params.row.lastName || ''}</>;
      },
    },
  ];

  return (
    <Grid item xs={12} mb={expanded ? -5 : 0}>
      <Divider sx={{ mx: -3 }} />
      <Accordion sx={{ mx: -4 }} disableGutters elevation={0} expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary
          expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
          sx={{
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(90deg)',
              transition: 'none !important',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(-90deg)',
              transition: 'none !important',
            },
            mb: -2,
          }}
          aria-controls='panel-connect-members-content'
          id='panel-connected-members-header'
        >
          <Typography sx={{ fontWeight: 500, color: 'text.secondary' }}>{t('member:associatedMembers')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              position: 'relative',
              '& .MuiPaper-root': {
                boxShadow: 'none',
              },
              mx: -2,
            }}
            ref={scrollRef}
          >
            <DataGrid<InvoiceAddressMemberResponse>
              autoHeight
              columns={columns}
              rows={rows}
              onRowClick={onRowClick}
              dataModel={data}
              checkboxSelection={false}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              onSelectionModelChange={(newSelectionModel) => {
                setSelectionModel(newSelectionModel);
              }}
              selectionModel={selectionModel}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                if (newSortModel.length > 0) {
                  setSortModel(newSortModel);
                } else {
                  setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
                }
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
